export type CamelCase<S extends string> =
  S extends `${infer Head}_${infer Tail}`
    ? `${Head}${Capitalize<CamelCase<Tail>>}`
    : S;

export type SnakeCase<S extends string> =
  S extends `${infer Head}${infer Tail}`
    ? Tail extends Uncapitalize<Tail>
      ? `${Uncapitalize<Head>}${SnakeCase<Tail>}`
      : `${Uncapitalize<Head>}_${SnakeCase<Uncapitalize<Tail>>}`
    : S;

export type CamelizeWithPreserve<T, P extends string = never> =
  T extends Array<infer U> ? Array<CamelizeWithPreserve<U, P>> :
  T extends object ? { [K in keyof T as K extends string ? (K extends P ? K : CamelCase<K>) : K]: CamelizeWithPreserve<T[K], P> } :
  T;

export type Camelize<T> = CamelizeWithPreserve<T>;

export type UnderscoreWithPreserve<T, P extends string = never> =
  T extends Array<infer U> ? Array<UnderscoreWithPreserve<U, P>> :
  T extends object ? { [K in keyof T as K extends string ? (K extends P ? K : SnakeCase<K>) : K]: UnderscoreWithPreserve<T[K], P> } :
  T;

export function toCamelCase(str: string): string {
  return str.replace(/[_-](\w)/g, (_, c) => c.toUpperCase());
}

export function toSnakeCase(str: string): string {
  return str
    .replace(/([A-Z])/g, "_$1")
    .replace(/[-\s]+/g, "_")
    .toLowerCase()
    .replace(/^_/, "");
}

export function camelize<T, P extends string = never>(obj: T, preserveKeys: readonly P[] = []): CamelizeWithPreserve<T, P> {
  if (Array.isArray(obj)) {
    return obj.map((v) => camelize(v, preserveKeys)) as unknown as CamelizeWithPreserve<T, P>;
  } else if (obj !== null && typeof obj === "object") {
    return Object.keys(obj).reduce((result, key) => {
      const newKey = preserveKeys.includes(key as P) ? key : toCamelCase(key);
      (result as any)[newKey] = camelize((obj as any)[key], preserveKeys);
      return result;
    }, {} as any) as CamelizeWithPreserve<T, P>;
  }
  return obj as unknown as CamelizeWithPreserve<T, P>;
}

export function underscore<T, P extends string = never>(obj: T, preserveKeys: readonly P[] = []): UnderscoreWithPreserve<T, P> {
  if (Array.isArray(obj)) {
    return obj.map((v) => underscore(v, preserveKeys)) as unknown as UnderscoreWithPreserve<T, P>;
  } else if (obj !== null && typeof obj === "object") {
    return Object.keys(obj).reduce((result, key) => {
      const newKey = preserveKeys.includes(key as P) ? key : toSnakeCase(key);
      (result as any)[newKey] = underscore((obj as any)[key], preserveKeys);
      return result;
    }, {} as any) as UnderscoreWithPreserve<T, P>;
  }
  return obj as unknown as UnderscoreWithPreserve<T, P>;
}
